'use client';

import { useTags } from '@/entities/tags/api/query';
import { Locale } from '@/shared/types/locale';
import {
  Button,
  Center,
  Checkbox,
  Flex,
  HStack,
  Popover,
  Portal,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useLocale, useTranslations } from 'next-intl';
import { useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { PiSlidersHorizontalLight } from 'react-icons/pi';

const TagSelect = () => {
  const { open, onClose, setOpen } = useDisclosure();
  const { data, isLoading } = useTags({ page: 1, limit: 10 });
  const locale = useLocale() as Locale;
  const t = useTranslations();

  const [tagsParam, setTagsParam] = useQueryState('tags');

  // ✅ LOCAL TEMP STATE (before Apply)
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // ✅ Sync from URL → local state on first load
  useEffect(() => {
    syncFromUrl();
  }, [tagsParam]);

  // ✅ Toggle checkbox
  const toggleTag = (id: string) => {
    setSelectedTags((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const syncFromUrl = () => {
    if (tagsParam) {
      setSelectedTags(tagsParam.split(','));
    } else {
      setSelectedTags([]);
    }
  };

  // ✅ APPLY BUTTON → writes to URL
  const handleApply = () => {
    if (selectedTags.length === 0) {
      setTagsParam(null); // ✅ removes ?tags from URL
    } else {
      setTagsParam(selectedTags.join(',')); // ✅ ?tags=a,b,c
    }

    onClose();
  };

  const clearTags = () => {
    setSelectedTags([]);
    setTagsParam(null);
  };
  return (
    <Flex direction="column" gap="2" alignItems="center">
      <Popover.Root
        modal
        positioning={{ placement: 'bottom-start' }}
        open={open}
        onOpenChange={(e) => {
          setOpen(e.open);
          if (!e.open) {
            syncFromUrl();
          }
        }}
      >
        <Popover.Trigger asChild>
          <Button
            size="xl"
            variant="outline"
            borderColor={
              selectedTags.length ? 'var(--primary-default)' : 'gray.200'
            }
            colorPalette="gray"
            aria-label="Filter"
            rounded="xl"
            width="56px"
            height="56px"
          >
            <PiSlidersHorizontalLight
              strokeWidth={3}
              color={selectedTags.length ? 'var(--primary-default)' : 'black'}
            />
          </Button>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content w="240px" borderRadius="md">
              <Popover.Arrow />
              <Popover.Body p="3">
                {isLoading ? (
                  <Center>
                    <Spinner />
                  </Center>
                ) : (
                  <>
                    <Popover.Title fontWeight="bold" fontSize="md" mb="4">
                      {t('filters')}
                    </Popover.Title>

                    <Flex direction="column" gap="2" mb="4">
                      {data?.shipper_tags.map((tag) => {
                        const checked = selectedTags.includes(tag.id);

                        return (
                          <Checkbox.Root
                            key={tag.id}
                            size="sm"
                            checked={checked}
                            onCheckedChange={() => toggleTag(tag.id)}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control
                              bg={
                                checked
                                  ? 'var(--primary-default)'
                                  : 'transparent'
                              }
                              borderColor={
                                checked ? 'var(--primary-default)' : 'gray.300'
                              }
                              _hover={{
                                borderColor: 'var(--primary-default)',
                              }}
                            />
                            <Checkbox.Label>{tag.title[locale]}</Checkbox.Label>
                          </Checkbox.Root>
                        );
                      })}
                    </Flex>

                    <HStack>
                      <Button
                        size="sm"
                        variant="subtle"
                        flex="1"
                        rounded="xl"
                        onClick={clearTags}
                      >
                        {t('clear')}
                      </Button>
                      <Button
                        size="sm"
                        flex="1"
                        rounded="xl"
                        onClick={handleApply}
                        bg="var(--primary-default)"
                      >
                        {t('apply')}
                      </Button>
                    </HStack>
                  </>
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      <Text fontSize="sm">{t('filters')}</Text>
    </Flex>
  );
};

export default TagSelect;
